import {
  Grid,
  IconButton,
  Typography,
  FormGroup,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  TextField,
} from '@mui/material';
import { FC, useState, useMemo } from 'react';
import { Comment, CommentOutlined } from '@mui/icons-material';
import {
  useStudentAttendanceActions,
  useStudentAttendanceState,
} from '../../contexts/studentAttendance.context';
import {
  ATTENDANCE_STATUS,
  TAttendanceByDateRecord,
  ICreateAttendanceRequest,
} from '../../../../../apis/__shared/attendance.interfaces';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import { useStudentAttendanceHook } from '../../hooks/useStudentAttendanceHook';
import { useStudentListHook } from '../../hooks/useStudentListHook';
import { TPaginationParams } from '../../../../../apis/__shared/shared.interfaces';
import { useResponsive } from '../../../../../hooks/useResponsiveHook';
import { usePostAttendanceMutationHook } from '../../hooks/usePostAttendanceMutationHook';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import { useStudentFiltersState } from '../../contexts/studentFilters.context';
import { useGradeAndFeeStructureHook } from '../../../institute/hooks/useGradeAndFeeStructureHook';
import isAppWithMock from 'src/utils/isAppWithMock';
import { shouldEnableStudentListQuery } from '../../utils/studentListApiFilters';
import { TStudentPrimary } from 'src/apis/students/students.interfaces';

type StudentAttendanceTrackerProps = {
  isOpen: boolean;
  onCloseAttendanceTrackerModal: () => void;
};

const StudentAttendanceTracker: FC<StudentAttendanceTrackerProps> = (props) => {
  const { isOpen, onCloseAttendanceTrackerModal } = props;
  const { translateText } = useTranslation();
  const { isMobile } = useResponsive();
  const { updateAttendanceTrackerPayload } = useStudentAttendanceActions();
  const {
    studentAttendanceAppState: { grade, section },
  } = useStudentAttendanceState();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const postAttendanceMutation = usePostAttendanceMutationHook();

  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString()
  );
  const [trackedStudents, setTrackedStudents] = useState<{
    [studentId: string]: TAttendanceByDateRecord;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [openCommentFields, setOpenCommentFields] = useState<{
    [studentId: string]: boolean;
  }>({});
  const [comments, setComments] = useState<{
    [studentId: string]: string;
  }>({});

  const paginationParams: TPaginationParams = useMemo(
    () => ({
      page: 1,
      limit: 1000, // Get all students for attendance tracking
    }),
    []
  );

  const fromDate = useMemo(() => {
    const date = new Date(selectedDate);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [selectedDate]);

  const toDate = useMemo(() => {
    const date = new Date(selectedDate);
    date.setHours(23, 59, 59, 999);
    return date;
  }, [selectedDate]);

  /** Same keys as GET /v1/students: classId / sectionId (stored on filters as grade / section). */
  const classIdValue = grade && grade !== 'select' ? grade : undefined;
  const sectionIdValue = section && section !== 'select' ? section : undefined;

  const filters = useStudentFiltersState();
  const withMock = isAppWithMock();
  const { data: classFeesData } = useGradeAndFeeStructureHook({
    enabled: !withMock,
  });
  const selectedClassIdForRoster =
    filters.grade !== 'select' ? filters.grade : undefined;

  const rosterListParams = useMemo(
    () => ({
      page: 1,
      limit: 1000,
      ...(selectedClassIdForRoster && { classId: selectedClassIdForRoster }),
      ...(filters.section !== 'select' && { sectionId: filters.section }),
    }),
    [selectedClassIdForRoster, filters.section]
  );

  const rosterQueryEnabled =
    isOpen &&
    shouldEnableStudentListQuery({
      selectedClassId: selectedClassIdForRoster,
      withMock,
      classFeesData,
    }) &&
    !!selectedClassIdForRoster;

  const { data: rosterPage } = useStudentListHook(rosterListParams, {
    enabled: rosterQueryEnabled,
  });
  const rosterStudents = useMemo(() => rosterPage?.data ?? [], [rosterPage?.data]);

  const { data: attendanceData } = useStudentAttendanceHook(
    paginationParams,
    classIdValue,
    sectionIdValue,
    fromDate,
    toDate,
    { enabled: isOpen }
  );

  // Extract unique students from attendance records
  const studentRecords = useMemo(() => {
    if (!attendanceData) return [];

    const studentMap = new Map<string, TAttendanceByDateRecord>();

    // Normalize to array-based structure: Array<{ date: string; records: TAttendanceRecord[] }>
    // Supports both array format (new) and object format (legacy)
    const recordsArray: Array<{
      date: string;
      records: TAttendanceByDateRecord[];
    }> = Array.isArray(attendanceData)
        ? attendanceData
        : Object.entries(attendanceData).map(([date, records]) => ({
          date,
          records,
        }));

    // Extract unique students from all attendance records across all dates
    recordsArray.forEach(({ records }) => {
      records.forEach((record: TAttendanceByDateRecord) => {
        const studentId = record.userDetails.id;
        if (studentId && !studentMap.has(studentId)) {
          studentMap.set(studentId, record);
        }
      });
    });

    return Array.from(studentMap.values());
  }, [attendanceData]);

  const mergedStudentRecords = useMemo(() => {
    const byId = new Map<string, TAttendanceByDateRecord>();
    studentRecords.forEach((r) => {
      const sid = r.userDetails?.id;
      if (sid) byId.set(sid, r);
    });

    const syntheticForStudent = (
      student: TStudentPrimary
    ): TAttendanceByDateRecord => ({
      attendanceId: `new-${student.id}`,
      userDetails: {
        ...student.basicDetails,
        id: student.id,
      },
      attendanceStatus: ATTENDANCE_STATUS.PRESENT,
      actions: {
        created: { userId: '', timestamp: Date.now() },
      },
    });

    if (rosterStudents.length > 0) {
      return rosterStudents
        .map((student) => {
          const sid = student.id;
          if (!sid) return null;
          return byId.get(sid) ?? syntheticForStudent(student);
        })
        .filter(Boolean) as TAttendanceByDateRecord[];
    }

    return studentRecords;
  }, [studentRecords, rosterStudents]);

  const attendanceOptions = [
    {
      label: translateText('students.attendance.present'),
      value: ATTENDANCE_STATUS.PRESENT,
      color: 'success',
    },
    {
      label: translateText('students.attendance.absent'),
      value: ATTENDANCE_STATUS.ABSENT,
      color: 'error',
    },
    {
      label: translateText('students.attendance.late'),
      value: ATTENDANCE_STATUS.SKIPPED,
      color: 'secondary',
    },
  ];

  const handleStudentTrackChange = (
    attendanceStatus: ATTENDANCE_STATUS,
    studentId: string,
    userDetails: TAttendanceByDateRecord['userDetails'],
    originalRecord?: TAttendanceByDateRecord
  ) => {
    const existingRecord = trackedStudents[studentId];
    setTrackedStudents({
      ...trackedStudents,
      [studentId]: {
        attendanceId: existingRecord?.attendanceId || originalRecord?.attendanceId || new Date().getTime().toString(),
        userDetails,
        attendanceStatus,
        comment: comments[studentId] || existingRecord?.comment || originalRecord?.comment,
        actions: existingRecord?.actions || originalRecord?.actions || {
          created: {
            userId: '',
            timestamp: Date.now(),
          },
        },
      },
    });
  };

  const handleCommentToggle = (studentId: string) => {
    setOpenCommentFields({
      ...openCommentFields,
      [studentId]: !openCommentFields[studentId],
    });
  };

  const handleCommentChange = (studentId: string, comment: string) => {
    setComments({
      ...comments,
      [studentId]: comment,
    });
    if (trackedStudents[studentId]) {
      setTrackedStudents({
        ...trackedStudents,
        [studentId]: {
          ...trackedStudents[studentId],
          comment,
        },
      });
    }
  };

  const onSaveAttendanceClickHandler = () => {
    if (Object.keys(trackedStudents).length < mergedStudentRecords.length) {
      setErrorMessage(translateText('students.attendance.selectAllStudents'));
      return;
    }
    setErrorMessage('');
    const attendanceRecords = Object.values(trackedStudents).map((record) => ({
      ...record,
      comment: comments[record.userDetails.id || ''] || record.comment,
    }));
    updateAttendanceTrackerPayload({
      [selectedDate]: attendanceRecords,
    });

    const payload: ICreateAttendanceRequest = {
      date: selectedDate,
      records: attendanceRecords.map((record) => ({
        studentId: record.userDetails.id || '',
        attendanceStatus: record.attendanceStatus,
        comment: record.comment,
      })),
      createdBy: loggedInUserId,
    };

    postAttendanceMutation.mutate(payload, {
      onSuccess: () => {
        onCloseAttendanceTrackerModal();
      },
      onError: (error) => {
        setErrorMessage(error?.response?.data?.message || 'Failed to save attendance');
      },
    });
  };

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={onCloseAttendanceTrackerModal}
      title={translateText('students.attendance.attendanceTracker')}
      footerPositiveAction={onSaveAttendanceClickHandler}
      footerPositiveActionText={translateText(
        'students.attendance.saveAttendance'
      )}
      footerMessage={
        errorMessage ? (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        ) : (
          ''
        )
      }
    >
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{
          mx: 'auto',
          height: 'calc(100% - var(--tab-group-height) * 2)',
          py: { xs: 1, sm: 2 },
        }}
        id="attendanceTrackerTable"
        data-testid="attendance-tracker-container"
      >
        <Grid
          item
          xs={12}
          sx={{
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Grid item xs={12} data-testid="attendance-tracker-date-label">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {translateText('students.attendance.selectDate')}
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <ControlledDateField
              name="selectedDate"
              id="selectedDate"
              label={translateText('students.attendance.date')}
              value={selectedDate}
              onChange={(date) => setSelectedDate(date.toISOString())}
            />
          </Grid>

          <Grid
            container
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              minHeight: { xs: 'auto', sm: 'var(--tab-group-height)' },
              px: { xs: 0, sm: 1 },
              mt: { xs: 1, sm: 0 },
            }}
            data-testid="attendance-tracker-header-desktop"
          >
            <Grid item xs={5}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                {translateText('students.attendance.student')}
              </Typography>
            </Grid>
            {attendanceOptions.map((eachAttendanceOption) => (
              <Grid item xs={2} key={eachAttendanceOption.value}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {eachAttendanceOption.label}
                </Typography>
              </Grid>
            ))}
            <Grid
              item
              xs={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CommentOutlined sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            height: `calc(100% - var(--tab-group-height))`,
            px: { xs: 0, sm: 1 },
            overflow: 'auto',
          }}
        >
          <Grid container spacing={{ xs: 1, sm: 0 }} data-testid="attendance-tracker-table-body">
            {mergedStudentRecords.map((record, index) => {
              const studentId = record.userDetails.id;
              if (!studentId) return null;

              return (
                <Grid
                  item
                  xs={12}
                  key={studentId}
                  sx={{
                    py: { xs: 1.5, sm: 1 },
                    borderTop:
                      index === 0
                        ? '1px solid var(--border_grey_light)'
                        : 'none',
                    borderBottom: '1px solid var(--border_grey_light)',
                    minHeight: { xs: 'auto', sm: 'var(--tab-group-height)' },
                  }}
                  data-testid="attendance-tracker-table-row"
                >
                  <FormGroup
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 0 },
                    }}
                  >
                    <Grid container spacing={{ xs: 1, sm: 0 }}>
                      <Grid
                        item
                        xs={12}
                        sm={5}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: { xs: 0.5, sm: 0 },
                        }}
                      >
                        <FormLabel
                          id={studentId}
                          sx={{
                            flex: 1,
                            fontSize: { xs: '0.875rem', sm: '0.875rem' },
                            fontWeight: { xs: 600, sm: 400 },
                          }}
                        >
                          {record.userDetails.firstName +
                            ' ' +
                            record.userDetails.lastName}
                        </FormLabel>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        sx={{
                          display: { xs: 'flex', sm: 'block' },
                          flexDirection: { xs: 'row', sm: 'row' },
                          alignItems: { xs: 'center', sm: 'center' },
                          gap: { xs: 1, sm: 0 },
                        }}
                      >
                        <RadioGroup
                          aria-labelledby={studentId}
                          name={studentId}
                          sx={{
                            flex: 1,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: { xs: 'space-between', sm: 'center' },
                          }}
                        >
                          {attendanceOptions.map((eachAttendanceOption) => (
                            <FormControlLabel
                              checked={
                                trackedStudents[studentId]?.attendanceStatus ===
                                eachAttendanceOption.value
                              }
                              value={eachAttendanceOption.value}
                              onChange={() => {
                                handleStudentTrackChange(
                                  eachAttendanceOption.value,
                                  studentId,
                                  record.userDetails,
                                  record
                                );
                              }}
                              key={eachAttendanceOption.value}
                              control={
                                <Radio
                                  size={isMobile ? 'medium' : 'small'}
                                  sx={{
                                    minHeight: { xs: '44px', sm: 'auto' },
                                    minWidth: { xs: '44px', sm: 'auto' },
                                  }}
                                  color={
                                    eachAttendanceOption.color as
                                    | 'primary'
                                    | 'secondary'
                                    | 'error'
                                    | 'info'
                                    | 'success'
                                    | 'warning'
                                    | 'default'
                                  }
                                />
                              }
                              label={isMobile ? eachAttendanceOption.label : ''}
                              sx={{
                                flex: { xs: 1, sm: 1 },
                                textAlign: 'center',
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                  fontSize: { xs: '0.75rem', sm: 'inherit' },
                                },
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={1}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: { xs: 'flex-start', sm: 'center' },
                          mt: { xs: 0.5, sm: 0 },
                        }}
                      >
                        <IconButton
                          color="primary"
                          aria-label={translateText(
                            'students.attendance.addComments'
                          )}
                          size={isMobile ? 'medium' : 'small'}
                          onClick={() => handleCommentToggle(studentId)}
                          data-testid={`comment-button-${studentId}`}
                          sx={{
                            minHeight: { xs: '44px', sm: 'auto' },
                            minWidth: { xs: '44px', sm: 'auto' },
                          }}
                        >
                          <Comment sx={{ fontSize: { xs: '1.25rem', sm: '1rem' } }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                    {openCommentFields[studentId] && (
                      <Grid item xs={12} sx={{ mt: { xs: 1, sm: 1 } }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          label={translateText('students.attendance.comment')}
                          value={
                            comments[studentId] ||
                            trackedStudents[studentId]?.comment ||
                            ''
                          }
                          onChange={(e) =>
                            handleCommentChange(studentId, e.target.value)
                          }
                          data-testid={`comment-field-${studentId}`}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '16px', sm: '14px' },
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </FormGroup>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </StructuredModal>
  );
};

export default StudentAttendanceTracker;
